import i18n from "../../i18n";
// Image upload + display helpers (Cloudflare R2 via business-service).
// Flow: ask business-service for a presigned PUT (createUploadUrl) → PUT the file
// straight to R2 → persist the returned KEY on the entity (e.g. business.image).
// Display: mediaUrl(key) → business-service's /media/<key> proxy (302 → signed GET).

const GQL_URL = process.env.REACT_APP_BUSINESS_SERVICE_BASE_URL || '';
// Strip the trailing /graphql to get the service origin (dev: http://localhost:8082;
// prod: /api/business). The /media proxy lives at <origin>/media/<key>.
const MEDIA_BASE = GQL_URL.replace(/\/graphql$/, '');

export interface UploadTarget {
    uploadUrl: string;
    key: string;
}

/** Ask the backend for a presigned PUT URL + the object key. folder = "<type>/<id>". */
export async function createUploadUrl(
    folder: string,
    contentType: string,
    token: string
): Promise<UploadTarget> {
    const res = await fetch(GQL_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt" },
        body: JSON.stringify({
            query: `mutation($folder: String!, $contentType: String!) {
                createUploadUrl(folder: $folder, contentType: $contentType) { uploadUrl key }
            }`,
            variables: { folder, contentType },
        }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e: { message: string }) => e.message).join(', '));
    return json.data.createUploadUrl as UploadTarget;
}

/**
 * Upload an image file to R2 and return its stored KEY (save this on the entity).
 * `folder` must be "<type>/<id>" with type ∈ business|user|service|category.
 */
export async function uploadImage(file: File, folder: string, token: string): Promise<string> {
    const { uploadUrl, key } = await createUploadUrl(folder, file.type, token);
    const put = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
    });
    if (!put.ok) throw new Error(`Falha no upload da imagem (${put.status})`);
    return key;
}

/**
 * Build a displayable URL from a stored value. Passes through full URLs and
 * legacy data:/blob: values untouched; bare R2 keys go through the /media proxy.
 */
export function mediaUrl(value?: string | null): string {
    if (!value) return '';
    if (/^(https?:|data:|blob:)/i.test(value)) return value; // external / legacy base64 / preview
    return `${MEDIA_BASE}/media/${value}`;
}
